import { useState, useEffect, useCallback } from "react";
import connectionService from "../services/connectionService";
import engagementService from "../services/engagementService";
import { userService } from "../services/userService";
import { FileUploadSection } from "./FileUploadSection";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Separator } from "./ui/separator";
import { Checkbox } from "./ui/checkbox";
import {
  FileText,
  Send,
  Users,
  User,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff,
  Download,
  Share2,
  Clock,
  Filter,
  Search,
  BarChart3,
  Lightbulb,
  AlertTriangle,
  Newspaper,
  Target,
  Globe,
  Lock,
  Paperclip,
  Pencil,
  Trash2,
  Upload,
  Save,
  Archive,
} from "lucide-react";
import { toast } from "sonner";
import { ResearchReport, ResearchRecipient, ResearchDocument } from "../interface";
import { researchService } from "../services/researchService";
import uploadService from "../services/uploadService";
import { MultiSelect } from "./ui/multi-select";
import { SECTOR_FILTER_OPTIONS } from "../utils/financialTaxonomy";
import { ResearchReportID } from "@/interface/ResearchReportID";

interface ResearchSharingProps {
  userRole: "analyst" | "investor" | "advisor";
  userId?: string;
}

const analystProfiles: Record<string, { name: string; firm: string }> = {
  ana1: { name: "Sarah Chen", firm: "Chen Analytics" },
  ana2: { name: "Alex Thompson", firm: "Thompson Research Group" },
  ana3: { name: "Maya Patel", firm: "Patel ESG Research" },
  user1: { name: "Sarah Chen", firm: "Chen Analytics" },
};

export function ResearchSharing({ userRole: roleProp, userId }: ResearchSharingProps) {
  const userRole = (roleProp || localStorage.getItem('userRole') || "investor").toLowerCase();
  const currentUserId = userId || localStorage.getItem('userId') || "user1";
  const [research, setResearch] = useState<ResearchReportID[]>([]);
  const [selectedResearch, setSelectedResearch] = useState<ResearchReportID | null>(null);
  const [currentUserProfile, setCurrentUserProfile] = useState<{ name: string; firm: string }>({
    name: analystProfiles[currentUserId]?.name ?? localStorage.getItem('fullName') ?? "Unknown Analyst",
    firm: analystProfiles[currentUserId]?.firm ?? "Independent"
  });
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        if (currentUserId && (currentUserProfile.name === "Unknown Analyst")) {
          const profileRes = await userService.getProfile(currentUserId);
          const profile = profileRes.data || profileRes;
          if (profile) {
            setCurrentUserProfile({
              name: profile.fullName || profile.name || localStorage.getItem('fullName') || "Unknown Analyst",
              firm: profile.firm || profile.company || "Independent"
            });
          }
        }
      } catch (error) {
        console.error("Error fetching current user profile:", error);
      }
    };

    fetchUserProfile();
    fetchResearchReports();
  }, [currentUserId]);
  const [shareType, setShareType] = useState<"individual" | "broadcast" | "public">("individual");
const [selectedRecipients, setSelectedRecipients] = useState<string[]>([]);
   const [searchQuery, setSearchQuery] = useState("");
   const [filterType, setFilterType] = useState<string>("all");
   const [isLoadingResearch, setIsLoadingResearch] = useState(false);
   const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
   const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
   const [isUploadingSelectedDocuments, setIsUploadingSelectedDocuments] = useState(false);
   const [editShareType, setEditShareType] = useState<"individual" | "broadcast" | "public">("individual");
   const [editSelectedRecipients, setEditSelectedRecipients] = useState<string[]>([]);
   const [editTitle, setEditTitle] = useState("");
  const [editType, setEditType] = useState<string>("report");
  const [editSummary, setEditSummary] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editPriority, setEditPriority] = useState<string>("medium");
  const [editTags, setEditTags] = useState("");
  const [editSecurities, setEditSecurities] = useState("");
  const [editSectors, setEditSectors] = useState<string[]>([]);

  const getRequesterQuery = (overrides: Record<string, string | number | undefined> = {}) => ({
    requesterId: currentUserId,
    requesterRole: userRole.toUpperCase(),
    page: 0,
    size: 100,
    ...overrides,
  });

  const normalizeResearchItem = (item: any): ResearchReportID | null => {
    if (!item) return null;

    const id = item.id || item.researchId || item.reportId;
    if (!id) return null;

    return {
      id,
      title: item.title || "Untitled Research",
      reportType: item.reportType || item.type || "report",
      summary: item.summary || "",
      content: item.content || "",
      author: item.author || {
        id: item.authorId || item.requesterId || currentUserId,
        role: item.authorRole || item.requesterRole || userRole,
        name: item.authorName || item.requesterName || "Unknown Analyst",
        firm: item.authorFirm || item.requesterCompany || "Independent",
      },
      publishedDate: item.publishedDate || item.createdAt || new Date().toISOString(),
      distributionType: item.distributionType || "public",
      priority: item.priority || "medium",
      status: item.status || "published",
      tags: item.tags || [],
      securities: item.securities || [],
      sectors: item.sectors || [],
      views: item.views || 0,
      recipients: item.recipients || [],
      documents: item.documents || item.attachments || [],
      readByCurrentUser: item.readByCurrentUser ?? false,
    };
  };

  const extractResearchList = (response: any) => {
    const data = response?.data ?? response;
    const items = Array.isArray(data)
      ? data
      : Array.isArray(data?.content)
        ? data.content
        : Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data?.records)
            ? data.records
            : Array.isArray(data?.researchItems)
              ? data.researchItems
              : [];

    return items.map((item: any) => normalizeResearchItem(item)).filter(Boolean) as ResearchReportID[];
  };

  const fetchResearchReports = useCallback(async () => {
    if (!currentUserId) return;

    try {
      setIsLoadingResearch(true);
      const response = await researchService.getResearchReports(getRequesterQuery({
        type: filterType === "all" ? "" : filterType,
        search: searchQuery,
      }));
      const normalizedResearch = extractResearchList(response);
      setResearch((prev) => {
        const merged = [...normalizedResearch];
        const existingIds = new Set(merged.map((m) => m.id));
        for (const existing of prev) {
          if (!existingIds.has(existing.id)) {
            if (existing.distributionType === "public") {
              merged.push(existing);
              existingIds.add(existing.id);
            }
          }
        }
        return merged;
      });
    } catch (error) {
      console.error("Error fetching research reports:", error);
      toast.error("Failed to load research reports");
    } finally {
      setIsLoadingResearch(false);
    }
  }, [currentUserId, userRole, filterType, searchQuery]);

  useEffect(() => {
    if (!selectedResearch) return;

    const refreshedResearch = research.find((item) => item.id === selectedResearch.id);
    if (refreshedResearch) {
      setSelectedResearch(refreshedResearch);
    }
  }, [research, selectedResearch?.id]);

  useEffect(() => {
    if (!selectedResearch) return;

    setEditTitle(selectedResearch.title);
    setEditType(selectedResearch.reportType);
    setEditSummary(selectedResearch.summary);
    setEditContent(selectedResearch.content);
    setEditPriority(selectedResearch.priority);
    setEditTags(selectedResearch.tags.join(", "));
    setEditSecurities(selectedResearch.securities.join(", "));
    setEditSectors(selectedResearch.sectors.map((sector) => SECTOR_FILTER_OPTIONS.find((option) => option.label === sector)?.value || sector));
    setEditShareType((selectedResearch.distributionType as "individual" | "broadcast" | "public") || "individual");
    setEditSelectedRecipients(selectedResearch.recipients?.map((r) => r.id) || []);
  }, [selectedResearch?.id, isEditDialogOpen]);

  // Publish form state
  const [newTitle, setNewTitle] = useState("");
  const [newType, setNewType] = useState<string>("report");
  const [newSummary, setNewSummary] = useState("");
  const [newContent, setNewContent] = useState("");
  const [newPriority, setNewPriority] = useState<string>("medium");
  const [newTags, setNewTags] = useState("");
  const [newSecurities, setNewSecurities] = useState("");
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);

  const [availableClients, setAvailableClients] = useState<ResearchRecipient[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);

  useEffect(() => {
    if (!isShareDialogOpen) {
      setNewTitle("");
      setNewType("report");
      setNewSummary("");
      setNewContent("");
      setNewPriority("medium");
      setNewTags("");
      setNewSecurities("");
      setSelectedSectors([]);
      setUploadedFiles([]);
      setSelectedRecipients([]);
    }
  }, [isShareDialogOpen]);

  useEffect(() => {
    const fetchConnectedEngagedClients = async () => {
      if (userRole !== "analyst") return;
      const isPublishOpen = isShareDialogOpen;
      const isEditOpen = isEditDialogOpen;
      if (!isPublishOpen && !isEditOpen) return;
      const activeShareType = isPublishOpen ? shareType : editShareType;
      if (activeShareType !== "individual") return;

      try {
        setIsLoadingClients(true);
        // Fetch active connections
        const connectionsRes = await connectionService.getConnections(currentUserId, { status: "ACTIVE" });
        const connections = connectionsRes.data || (Array.isArray(connectionsRes.connections) ? connectionsRes.connections : []);

        // Fetch agreements
        const agreementsRes = await engagementService.getMyAgreements(currentUserId);
        const agreements = agreementsRes.data || agreementsRes;

        const clientsMap = new Map<string, ResearchRecipient>();

        // 1. Process active connections
        if (Array.isArray(connections)) {
          connections.forEach((conn: any) => {
            const isRequester = String(conn.requesterId) === String(currentUserId);
            const otherId = isRequester ? conn.recipientId : conn.requesterId;
            const otherName = isRequester ? conn.recipientFullName : conn.requesterFullName;
            const otherRole = (isRequester ? conn.recipientRole : conn.requesterRole)?.toLowerCase();
            const otherFirm = isRequester ? conn.recipientCompany : conn.requesterCompany;

            if (otherRole === "investor" || otherRole === "advisor") {
              clientsMap.set(otherId, {
                id: otherId,
                name: otherName || `User ${otherId.substring(0, 4)}`,
                role: otherRole as "investor" | "advisor",
                firm: otherFirm || "Independent",
                readAt: null,
                responded: false
              });
            }
          });
        }

        // 2. Process agreements to find any additional engaged clients/users
        if (Array.isArray(agreements)) {
          for (const agreement of agreements) {
            if (agreement.status === "INVALIDATED") continue;

            const isRequester = String(agreement.requesterId) === String(currentUserId);
            const otherId = isRequester ? agreement.recipientId : agreement.requesterId;

            if (!clientsMap.has(otherId)) {
              const otherName = agreement.clientName || agreement.recipientFullName || agreement.requesterFullName || `User ${otherId.substring(0, 4)}`;
              try {
                const profileRes = await userService.getProfile(otherId);
                const profile = profileRes.data || profileRes;
                if (profile) {
                  const role = (profile.role || "investor").toLowerCase();
                  if (role === "investor" || role === "advisor") {
                    clientsMap.set(otherId, {
                      id: otherId,
                      name: profile.fullName || profile.name || otherName,
                      role: role as "investor" | "advisor",
                      firm: profile.firm || profile.company || "Independent",
                      readAt: null,
                      responded: false
                    });
                  }
                }
              } catch (err) {
                console.error(`Failed to fetch profile for client ${otherId}:`, err);
                clientsMap.set(otherId, {
                  id: otherId,
                  name: otherName,
                  role: "investor",
                  firm: "Independent",
                  readAt: null,
                  responded: false
                });
              }
            }
          }
        }

        const retrievedClients = Array.from(clientsMap.values());
        if (retrievedClients.length > 0) {
          setAvailableClients(retrievedClients);
        }
      } catch (error) {
        console.error("Error fetching connected and engaged clients:", error);
      } finally {
        setIsLoadingClients(false);
      }
    };

    fetchConnectedEngagedClients();
  }, [userRole, shareType, editShareType, isShareDialogOpen, isEditDialogOpen, currentUserId]);

  const handleShareResearch = async () => {
    if (!newTitle.trim()) {
      toast.error("Please enter a research title");
      return;
    }
    if (!newSummary.trim()) {
      toast.error("Please enter a summary");
      return;
    }
    if (!newContent.trim()) {
      toast.error("Please enter research content");
      return;
    }
    if (selectedRecipients.length === 0 && shareType === "individual") {
      toast.error("Please select at least one recipient");
      return;
    }

    const recipientNames = availableClients
      .filter((c) => selectedRecipients.includes(c.id))
      .map((c) => c.name)
      .join(", ");

    // Upload files to S3 first
    const attachmentsList: ResearchDocument[] = [];
    if (uploadedFiles.length > 0) {
      const uploadToastId = toast.loading("Uploading documents to S3...");
      for (let idx = 0; idx < uploadedFiles.length; idx++) {
        const file = uploadedFiles[idx];
        try {
          const { url } = await uploadService.uploadFile(file, 'research');
          attachmentsList.push({
            researchId: "", // Will be associated by backend
            uploadedByUserId: currentUserId,
            uploadedByName: currentUserProfile.name,
            documentName: file.name,
            documentType: file.name.split('.').pop()?.toUpperCase() || "FILE",
            fileUrl: url,
            fileSizeBytes: file.size,
            fileMimeType: file.type || "application/octet-stream",
            isSensitive: false,
            uploadedAt: new Date().toISOString(),
            description: `Uploaded document: ${file.name}`
          } as any);
        } catch (uploadErr: any) {
          console.error(`Failed to upload file ${file.name}:`, uploadErr);
          toast.dismiss(uploadToastId);
          toast.error(`Failed to upload ${file.name} to S3`);
          return;
        }
      }
      toast.dismiss(uploadToastId);
    }

    const newResearchItem: ResearchReport = {
      id: "", // Will be assigned by backend
      title: newTitle,
      reportType: newType,
      summary: newSummary,
      content: newContent,
      author: {
        id: currentUserId,
        role: "analyst",
        name: currentUserProfile.name,
        firm: currentUserProfile.firm,
      },
      publishedDate: new Date().toISOString().split('T')[0] + " " + new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }),
      distributionType: shareType,
      recipients: shareType === "individual" 
        ? availableClients.filter(c => selectedRecipients.includes(c.id))
        : shareType === "broadcast"
          ? availableClients
          : [],
      tags: newTags ? newTags.split(",").map(t => t.trim()).filter(Boolean) : [],
      securities: newSecurities ? newSecurities.split(",").map(s => s.trim()).filter(Boolean) : [],
      sectors: selectedSectors.map(val => SECTOR_FILTER_OPTIONS.find(opt => opt.value === val)?.label || val),
      priority: newPriority,
      status: "published",
      views: 0,
      documents: attachmentsList,
      readByCurrentUser: false
    };

    try {
    const response = await researchService.publishResearch(newResearchItem);
    const data = response.data || response;
    const normalizedData = normalizeResearchItem(data) || { ...newResearchItem, id: data.reportId } as ResearchReportID;
    setResearch((prev) => [normalizedData, ...prev.filter((item) => item.id !== normalizedData.id)]);
    setSelectedResearch(normalizedData);
    console.log("Research report published successfully:", data);
    toast.success("Research report published to API successfully!");
    } catch (error: any) {
      console.error("Error publishing research report to API:", error);
      toast.error(`Published locally, but API call failed: ${error.message}`);
    }

    if (shareType === "broadcast") {
      toast.success(`Research shared with all ${availableClients.length} clients`);
    } else if (shareType === "public") {
      toast.success("Research published publicly");
    } else {
      toast.success(`Research shared with ${recipientNames}`);
    }

    setIsShareDialogOpen(false);
    setSelectedRecipients([]);
    setNewTitle("");
    setNewType("report");
    setNewSummary("");
    setNewContent("");
    setNewPriority("medium");
    setNewTags("");
    setNewSecurities("");
    setSelectedSectors([]);
    setUploadedFiles([]);
  };

  const uploadSelectedDocuments = async (files?: File[]) => {
    if (!files || files.length === 0) return;
    if (!selectedResearch) return;
    if (selectedResearch.author.id !== currentUserId) {
      toast.error("Only the author can add documents to this research");
      return;
    }

    try {
      setIsUploadingSelectedDocuments(true);
      const newDocuments: ResearchDocument[] = [];

      for (const file of files) {
        try {
          const { url } = await uploadService.uploadFile(file, "research");
          newDocuments.push({
            researchId: selectedResearch.id,
            uploadedByUserId: currentUserId,
            uploadedByName: currentUserProfile.name,
            documentName: file.name,
            documentType: file.name.split(".").pop()?.toUpperCase() || "FILE",
            fileUrl: url,
            fileSizeBytes: file.size,
            fileMimeType: file.type || "application/octet-stream",
            isSensitive: false,
            uploadedAt: new Date().toISOString(),
            description: `Uploaded document: ${file.name}`,
          } as any);
        } catch (uploadErr: any) {
          console.error(`Failed to upload file ${file.name}:`, uploadErr);
          toast.error(`Failed to upload ${file.name}`);
          return;
        }
      }

      const response = await researchService.addResearchDocuments(selectedResearch.id, newDocuments);

      const data = response.data || response;
      const normalizedData = normalizeResearchItem(data) || {
        ...selectedResearch,
        documents: data.documents,
      };

      setResearch((prev) => prev.map((item) => (item.id === normalizedData.id ? normalizedData : item)));
      setSelectedResearch(normalizedData);
      // Files uploaded directly via onFilesChange
      toast.success("Documents uploaded successfully");
    } catch (error: any) {
      console.error("Error uploading selected research documents:", error);
      toast.error(error.message || "Failed to upload documents");
    } finally {
      setIsUploadingSelectedDocuments(false);
    }
  };

  const updateSelectedResearch = async () => {
    if (!selectedResearch) return;
    if (selectedResearch.author.id !== currentUserId) {
      toast.error("Only the author can update this research");
      return;
    }
    if (!editTitle.trim()) {
      toast.error("Please enter a research title");
      return;
    }
    if (!editSummary.trim()) {
      toast.error("Please enter a summary");
      return;
    }
    if (!editContent.trim()) {
      toast.error("Please enter research content");
      return;
    }

    try {
      const updatedResearch = {
        ...selectedResearch,
        title: editTitle.trim(),
        reportType: editType,
        summary: editSummary.trim(),
        content: editContent.trim(),
        priority: editPriority,
        tags: editTags.split(",").map((tag) => tag.trim()).filter(Boolean),
        securities: editSecurities.split(",").map((security) => security.trim()).filter(Boolean),
        sectors: editSectors.map((value) => SECTOR_FILTER_OPTIONS.find((option) => option.value === value)?.label || value),
        distributionType: editShareType,
        recipients: editShareType === "individual"
          ? availableClients.filter((c) => editSelectedRecipients.includes(c.id))
          : editShareType === "broadcast"
            ? availableClients
            : [],
      };

      const response = await researchService.updateResearch(
        selectedResearch.id,
        updatedResearch
      );
      const data = response.data || response;
      const normalizedData = normalizeResearchItem(data) || updatedResearch;

      setResearch((prev) => prev.map((item) => (item.id === normalizedData.id ? normalizedData : item)));
      setSelectedResearch(normalizedData);
      setIsEditDialogOpen(false);
      toast.success("Research updated successfully");
    } catch (error: any) {
      console.error("Error updating selected research:", error);
      toast.error(error.message || "Failed to update research");
    }
  };

  const deleteSelectedResearch = async () => {
    if (!selectedResearch) return;
    if (selectedResearch.author.id !== currentUserId) {
      toast.error("Only the author can delete this research");
      return;
    }

    try {
      await researchService.deleteResearch(selectedResearch.id, selectedResearch.author.id);
      setResearch((prev) => prev.filter((item) => item.id !== selectedResearch.id));
      setSelectedResearch(null);
      setIsDeleteDialogOpen(false);
      toast.success("Research deleted successfully");
    } catch (error: any) {
      console.error("Error deleting selected research:", error);
      toast.error(error.message || "Failed to delete research");
    }
  };

  const toggleRecipient = (id: string) => {
    setSelectedRecipients((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const toggleEditRecipient = (id: string) => {
    setEditSelectedRecipients((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]
    );
  };

  const handleSelectResearch = async (item: ResearchReportID) => {
    if (!item.readByCurrentUser && userRole !== "analyst") {
      try {
        await researchService.updateReadViews(item.id, currentUserId);
        setResearch((prev) =>
          prev.map((r) =>
            r.id === item.id
              ? { ...r, readByCurrentUser: true, views: r.views + 1 }
              : r
          )
        );
      } catch (error) {
        console.error("Error updating read status:", error);
      }
    }
    setSelectedResearch(item);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "report":
        return FileText;
      case "alert":
        return AlertTriangle;
      case "recommendation":
        return Target;
      case "commentary":
        return Newspaper;
      case "analysis":
        return BarChart3;
      default:
        return FileText;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "report":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
      case "alert":
        return "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300";
      case "recommendation":
        return "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300";
      case "commentary":
        return "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300";
      case "analysis":
        return "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300";
      case "medium":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300";
      case "low":
        return "bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getDistributionIcon = (type: string) => {
    switch (type) {
      case "individual":
        return <User className="h-3 w-3" />;
      case "broadcast":
        return <Users className="h-3 w-3" />;
      case "public":
        return <Globe className="h-3 w-3" />;
      default:
        return <Globe className="h-3 w-3" />;
    }
  };

  // Filter research based on user role
  const filteredResearch = research.filter((item) => {
    // Type filter
    if (filterType !== "all" && item.reportType !== filterType) return false;

    // Search filter
    if (searchQuery && 
        !item.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !item.summary.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !item.sectors?.some((s) => s.toLowerCase().includes(searchQuery.toLowerCase()))) {
      return false;
    }

    // Role-based filtering
    if (userRole === "analyst") {
      return true; // Analysts see all their research
    } else {
      // Investors/Advisors only see research shared with them or public
      return (
        item.distributionType === "public" ||
        item.recipients?.some((r) => r.id === currentUserId)
      );
    }
  });

  const canManageSelectedResearch = selectedResearch?.author.id === currentUserId;

  const unreadCount = filteredResearch.filter(
    (item) => !item.readByCurrentUser
  ).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl">
            {userRole === "analyst" ? "Research Distribution" : "Research Library"}
          </h2>
          <p className="text-muted-foreground">
            {userRole === "analyst"
              ? "Share research and insights with your clients"
              : "Access research and insights from your analysts"}
          </p>
        </div>
        {userRole === "analyst" && (
          <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Share2 className="mr-2 h-4 w-4" />
                Publish Research
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Publish New Research</DialogTitle>
                <DialogDescription>
                  Write and distribute research to individual clients, broadcast to all, or publish publicly
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Author banner */}
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="text-xs">
                      {(currentUserProfile.name ?? "AN")
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium">
                      Publishing as{" "}
                      <span className="text-primary">
                        {currentUserProfile.name ?? "Unknown Analyst"}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {currentUserProfile.firm ?? "Independent"}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Research Title *</Label>
                  <Input
                    placeholder="e.g., Q4 2025 Market Outlook"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                  />
                </div>

                 <div className="space-y-2">
                  <Label>Research Type *</Label>
                  <Select value={newType} onValueChange={(v) => setNewType(v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="report">Research Report</SelectItem>
                      <SelectItem value="alert">Market Alert</SelectItem>
                      <SelectItem value="recommendation">Stock Recommendation</SelectItem>
                      <SelectItem value="commentary">Market Commentary</SelectItem>
                      <SelectItem value="analysis">Sector Analysis</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Summary * <span className="text-muted-foreground font-normal text-xs">(shown in client inbox)</span></Label>
                  <Textarea
                    placeholder="Brief summary of the research..."
                    rows={2}
                    value={newSummary}
                    onChange={(e) => setNewSummary(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Full Content * <span className="text-muted-foreground font-normal text-xs">(supports markdown)</span></Label>
                  <Textarea
                    placeholder="# Executive Summary&#10;&#10;## Key Findings&#10;- Finding 1&#10;&#10;## Recommendation&#10;..."
                    rows={7}
                    value={newContent}
                    onChange={(e) => setNewContent(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={newPriority} onValueChange={(v) => setNewPriority(v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Tags</Label>
                    <Input
                      placeholder="Technology, AI, Growth"
                      value={newTags}
                      onChange={(e) => setNewTags(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Securities Covered <span className="text-muted-foreground font-normal text-xs">(optional)</span></Label>
                  <Input
                    placeholder="NVDA, MSFT, AAPL"
                    value={newSecurities}
                    onChange={(e) => setNewSecurities(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sectors Covered <span className="text-muted-foreground font-normal text-xs">(optional)</span></Label>
                  <MultiSelect
                    options={SECTOR_FILTER_OPTIONS.map((option) => ({
                      value: option.value,
                      label: option.label,
                    }))}
                    selected={selectedSectors}
                    onChange={setSelectedSectors}
                    placeholder="Select sectors..."
                  />
                </div>

                <FileUploadSection 
                  id="research-publish-upload"
                  label="Attach Files (Optional)"
                  helpText="Attach PDF reports, spreadsheets, charts, or any other relevant documents to accompany this research." 
                  onFilesChange={(files) => setUploadedFiles(files)}
                />

                <Separator />

                <div className="space-y-3">
                  <Label>Distribution Method</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <Card
                      className={`cursor-pointer transition-all ${
                        shareType === "individual"
                          ? "border-primary ring-2 ring-primary/20"
                          : "hover:border-primary/50"
                      }`}
                      onClick={() => setShareType("individual")}
                    >
                      <CardContent className="p-3">
                        <div className="flex flex-col items-center gap-2 text-center">
                          <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                            <User className="h-4 w-4 text-blue-700 dark:text-blue-300" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">Individual</p>
                            <p className="text-xs text-muted-foreground">Select clients</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card
                      className={`cursor-pointer transition-all ${
                        shareType === "broadcast"
                          ? "border-primary ring-2 ring-primary/20"
                          : "hover:border-primary/50"
                      }`}
                      onClick={() => setShareType("broadcast")}
                    >
                      <CardContent className="p-3">
                        <div className="flex flex-col items-center gap-2 text-center">
                          <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                            <Users className="h-4 w-4 text-purple-700 dark:text-purple-300" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">Broadcast</p>
                            <p className="text-xs text-muted-foreground">All clients</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card
                      className={`cursor-pointer transition-all ${
                        shareType === "public"
                          ? "border-primary ring-2 ring-primary/20"
                          : "hover:border-primary/50"
                      }`}
                      onClick={() => setShareType("public")}
                    >
                      <CardContent className="p-3">
                        <div className="flex flex-col items-center gap-2 text-center">
                          <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                            <Globe className="h-4 w-4 text-green-700 dark:text-green-300" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">Public</p>
                            <p className="text-xs text-muted-foreground">Anyone can view</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {shareType === "individual" && (
                  <div className="space-y-3">
                    <Label>Select Recipients ({selectedRecipients.length} selected)</Label>
                    <div className="border rounded-lg p-4 max-h-64 overflow-y-auto space-y-2">
                      {isLoadingClients ? (
                        <div className="py-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                          <Clock className="h-4 w-4 animate-spin text-primary" />
                          <span>Loading connected clients...</span>
                        </div>
                      ) : (
                        <>
                          <div className="mb-3">
                            <Input
                              placeholder="Search clients..."
                              className="mb-2"
                              value={clientSearchQuery}
                              onChange={(e) => setClientSearchQuery(e.target.value)}
                            />
                          </div>

                          {availableClients.filter((c) =>
                            c.name.toLowerCase().includes(clientSearchQuery.toLowerCase())
                          ).length === 0 ? (
                            <div className="text-center py-4 text-sm text-muted-foreground">
                              No clients found
                            </div>
                          ) : (
                            <>
                              {/* Investors Section */}
                              {availableClients
                                .filter((c) => c.role === "investor" && c.name.toLowerCase().includes(clientSearchQuery.toLowerCase()))
                                .length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-muted-foreground mb-2">
                                    INVESTORS
                                  </p>
                                  {availableClients
                                    .filter((c) => c.role === "investor" && c.name.toLowerCase().includes(clientSearchQuery.toLowerCase()))
                                    .map((client) => (
                                      <div
                                        key={client.id}
                                        className="flex items-center space-x-3 p-2 hover:bg-muted rounded-lg cursor-pointer"
                                        onClick={() => toggleRecipient(client.id)}
                                      >
                                        <Checkbox
                                          checked={selectedRecipients.includes(client.id)}
                                          onCheckedChange={() => toggleRecipient(client.id)}
                                        />
                                        <Avatar className="h-8 w-8">
                                          <AvatarFallback className="text-xs">
                                            {client.name
                                              .split(" ")
                                              .map((n) => n[0])
                                              .join("")}
                                          </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1">
                                          <p className="text-sm font-medium">{client.name}</p>
                                          <Badge variant="outline" className="text-xs">
                                            Investor
                                          </Badge>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              )}

                              {availableClients.filter((c) => c.role === "investor" && c.name.toLowerCase().includes(clientSearchQuery.toLowerCase())).length > 0 &&
                                availableClients.filter((c) => c.role === "advisor" && c.name.toLowerCase().includes(clientSearchQuery.toLowerCase())).length > 0 && (
                                  <Separator className="my-3" />
                                )}

                              {/* Advisors Section */}
                              {availableClients
                                .filter((c) => c.role === "advisor" && c.name.toLowerCase().includes(clientSearchQuery.toLowerCase()))
                                .length > 0 && (
                                <div>
                                  <p className="text-xs font-semibold text-muted-foreground mb-2">
                                    ADVISORS
                                  </p>
                                  {availableClients
                                    .filter((c) => c.role === "advisor" && c.name.toLowerCase().includes(clientSearchQuery.toLowerCase()))
                                    .map((client) => (
                                      <div
                                        key={client.id}
                                        className="flex items-center space-x-3 p-2 hover:bg-muted rounded-lg cursor-pointer"
                                        onClick={() => toggleRecipient(client.id)}
                                      >
                                        <Checkbox
                                          checked={selectedRecipients.includes(client.id)}
                                          onCheckedChange={() => toggleRecipient(client.id)}
                                        />
                                        <Avatar className="h-8 w-8">
                                          <AvatarFallback className="text-xs">
                                            {client.name
                                              .split(" ")
                                              .map((n) => n[0])
                                              .join("")}
                                          </AvatarFallback>
                                        </Avatar>
                                        <div className="flex-1">
                                          <p className="text-sm font-medium">{client.name}</p>
                                          {client.firm && (
                                            <p className="text-xs text-muted-foreground">{client.firm}</p>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {shareType === "broadcast" && (
                  <div className="p-4 bg-muted rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium mb-1">Broadcast Distribution</p>
                        <p className="text-sm text-muted-foreground">
                          This research will be shared with all {availableClients.length} clients
                          ({availableClients.filter((c) => c.role === "investor").length}{" "}
                          investors, {availableClients.filter((c) => c.role === "advisor").length}{" "}
                          advisors)
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {shareType === "public" && (
                  <div className="p-4 bg-muted rounded-lg">
                    <div className="flex items-start gap-3">
                      <Globe className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium mb-1">Public Publication</p>
                        <p className="text-sm text-muted-foreground">
                          This research will be visible to all platform users including investors
                          and advisors you may not be directly connected with.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsShareDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleShareResearch}>
                  <Send className="mr-2 h-4 w-4" />
                  Publish &amp; Distribute
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {userRole === "analyst" ? "Total Research" : "Available Research"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{filteredResearch.length}</div>
            <p className="text-xs text-muted-foreground">
              {userRole === "analyst" ? "Published items" : "In your library"}
            </p>
          </CardContent>
        </Card>

        {userRole !== "analyst" && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Unread</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl">{unreadCount}</div>
              <p className="text-xs text-muted-foreground">New research items</p>
            </CardContent>
          </Card>
        )}

        {userRole === "analyst" && (
          <>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Total Views</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl">
                  {research.reduce((sum, r) => sum + r.views, 0)}
                </div>
                <p className="text-xs text-muted-foreground">Across all research</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Avg Read Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl">
                  {research.length > 0
                    ? Math.round(
                        (research.reduce(
                          (sum, r) => sum + (r.recipients ? r.recipients.filter(rec => rec.readAt).length : 0),
                          0
                        ) /
                          research.reduce(
                            (sum, r) => sum + (r.recipients?.length || 1),
                            0
                          )) *
                          100
                      )
                    : 0}
                  %
                </div>
                <p className="text-xs text-muted-foreground">Client engagement</p>
              </CardContent>
            </Card>
          </>
        )}

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">This Week</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl">
              {
                filteredResearch.filter((r) => {
                  const date = new Date(r.publishedDate);
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return date > weekAgo;
                }).length
              }
            </div>
            <p className="text-xs text-muted-foreground">Recent publications</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search research..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="report">Reports</SelectItem>
                <SelectItem value="alert">Alerts</SelectItem>
                <SelectItem value="recommendation">Recommendations</SelectItem>
                <SelectItem value="commentary">Commentary</SelectItem>
                <SelectItem value="analysis">Analysis</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Research List and Detail View */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Research List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Research Items</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {isLoadingResearch ? (
                <div className="text-center py-8 text-muted-foreground flex items-center justify-center gap-2">
                  <Clock className="h-4 w-4 animate-spin text-primary" />
                  <p>Loading research...</p>
                </div>
              ) : filteredResearch.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No research found</p>
                </div>
              ) : (
                filteredResearch.map((item) => {
                  const Icon = getTypeIcon(item.reportType);
                  const isUnread = !item.readByCurrentUser;

                  return (
                    <div
                      key={item.id}
                      className={`p-4 border rounded-lg cursor-pointer transition-all hover:bg-muted/50 ${
                        selectedResearch?.id === item.id ? "bg-muted border-primary" : ""
                      } ${userRole !== "analyst" ? "border-l-4 border-l-primary" : ""}`}
                      onClick={() => handleSelectResearch(item)}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`p-2 rounded-lg ${getTypeColor(item.reportType)}`}
                        >
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h4 className="font-medium line-clamp-2 text-sm">
                              {item.title}
                            </h4>
                            {isUnread && userRole !== "analyst" && (
                              <div className="h-2 w-2 bg-primary rounded-full flex-shrink-0 mt-1" />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                            {item.summary}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={getTypeColor(item.reportType)}>
                              {item.reportType}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {getDistributionIcon(item.distributionType)}
                              <span className="ml-1">{item.distributionType}</span>
                            </Badge>
                            {item.priority !== "low" && (
                              <Badge className={getPriorityColor(item.priority)}>
                                {item.priority}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {item.publishedDate}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>

        {/* Research Detail */}
        <div className="lg:col-span-2">
          {selectedResearch ? (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getTypeColor(selectedResearch.reportType)}>
                        {selectedResearch.reportType}
                      </Badge>
                      <Badge className={getPriorityColor(selectedResearch.priority)}>
                        {selectedResearch.priority} priority
                      </Badge>
                      <Badge variant="outline">
                        {getDistributionIcon(selectedResearch.distributionType)}
                        <span className="ml-1">{selectedResearch.distributionType}</span>
                      </Badge>
                      <Badge
                        className={
                          selectedResearch.status?.toLowerCase() === "published"
                            ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                            : "bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300"
                        }
                      >
                        {selectedResearch.status?.toLowerCase() === "published" ? "Published" : "Archived"}
                      </Badge>
                    </div>
                    <CardTitle className="mb-2">{selectedResearch.title}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {selectedResearch.summary}
                    </p>
                  </div>
                  {canManageSelectedResearch && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsEditDialogOpen(true)}
                      >
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setIsDeleteDialogOpen(true)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </Button>
                      {selectedResearch.status?.toLowerCase() === "published" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            if (!confirm("Archive this research?")) return;
                            try {
                              await researchService.updateResearchStatus(selectedResearch.id, "ARCHIVED");
                              setResearch((prev) =>
                                prev.map((item) =>
                                  item.id === selectedResearch.id
                                    ? { ...item, status: "ARCHIVED" }
                                    : item
                                )
                              );
                              setSelectedResearch((prev) =>
                                prev ? { ...prev, status: "ARCHIVED" } : null
                              );
                              toast.success("Research archived");
                            } catch (err: any) {
                              toast.error(err.message || "Failed to archive research");
                            }
                          }}
                        >
                          <Archive className="mr-2 h-4 w-4" />
                          Archive
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            if (!confirm("Publish this research?")) return;
                            try {
                              await researchService.updateResearchStatus(selectedResearch.id, "PUBLISHED");
                              setResearch((prev) =>
                                prev.map((item) =>
                                  item.id === selectedResearch.id
                                    ? { ...item, status: "PUBLISHED" }
                                    : item
                                )
                              );
                              setSelectedResearch((prev) =>
                                prev ? { ...prev, status: "PUBLISHED" } : null
                              );
                              toast.success("Research published");
                            } catch (err: any) {
                              toast.error(err.message || "Failed to publish research");
                            }
                          }}
                        >
                          <Archive className="mr-2 h-4 w-4" />
                          Publish
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Author Info */}
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback>
                      {selectedResearch.author.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{selectedResearch.author.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedResearch.author.firm}
                    </p>
                  </div>
                  <div className="ml-auto text-sm text-muted-foreground">
                    {selectedResearch.publishedDate}
                  </div>
                </div>

                <Separator />

                {/* Tags and Coverage */}
                {(selectedResearch.tags.length > 0 ||
                  selectedResearch.securities ||
                  selectedResearch.sectors) && (
                  <div className="space-y-3">
                    {selectedResearch.tags.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Tags</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedResearch.tags.map((tag, idx) => (
                            <Badge key={idx} variant="outline">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedResearch.securities && (
                      <div>
                        <Label className="text-xs text-muted-foreground">
                          Securities Covered
                        </Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedResearch.securities.map((symbol, idx) => (
                            <Badge key={idx} variant="secondary">
                              {symbol}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedResearch.sectors && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Sectors</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedResearch.sectors.map((sector, idx) => (
                            <Badge key={idx} variant="outline">
                              {sector}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <Separator />

                {/* Content */}
                <div>
                  <Label className="mb-2">Research Content</Label>
                  <div className="prose prose-sm max-w-none dark:prose-invert mt-2 p-4 bg-muted/30 rounded-lg">
                    <pre className="whitespace-pre-wrap font-sans text-sm">
                      {selectedResearch.content}
                    </pre>
                  </div>
                </div>

                {/* Attachments */}
                {selectedResearch.documents && (selectedResearch.documents.length > 0 || canManageSelectedResearch) && (
                  <>
                    <Separator />
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>Attachments</Label>
                       {canManageSelectedResearch && (
                          <Badge variant="secondary">Author can upload</Badge>
                        )}
                      </div>

                      {Array.isArray(selectedResearch.documents) && selectedResearch.documents.length > 0 ? (
                         <div className="grid grid-cols-1 gap-2">
                            {selectedResearch.documents.map((attachment) => (
                              <div
                                key={attachment.id}
                                className="flex items-center justify-between p-2 border rounded-md hover:bg-muted/50 transition-colors group"
                              >
                                <button
                                  type="button"
                                  onClick={() => uploadService.downloadFile(attachment.fileUrl)}
                                  className="flex items-center gap-3 overflow-hidden flex-1 text-left"
                                >
                                  <div className="p-1.5 bg-primary/10 rounded-md">
                                    <Paperclip className="h-3.5 w-3.5 text-primary" />
                                  </div>
                                  <div className="overflow-hidden">
                                    <p className="text-xs font-medium truncate" title={attachment.documentName}>
                                      {attachment.documentName}
                                    </p>
                                    {attachment.fileSizeBytes && (
                                      <p className="text-[10px] text-muted-foreground">
                                        {(attachment.fileSizeBytes / 1024).toFixed(1)} KB
                                      </p>
                                    )}
                                  </div>
                                </button>
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => uploadService.downloadFile(attachment.fileUrl)}
                                    className="p-1.5 hover:bg-muted rounded-md transition-colors"
                                    title="Download"
                                  >
                                    <Download className="h-3.5 w-3.5 text-muted-foreground hover:text-primary transition-colors" />
                                  </button>
                                  {canManageSelectedResearch && (
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        if (!confirm("Delete this attachment?")) return;
                                        try {
                                          await researchService.deleteAttachment(selectedResearch.id, attachment.id, currentUserId);
                                          setResearch((prev) =>
                                            prev.map((item) =>
                                              item.id === selectedResearch.id
                                                ? { ...item, documents: item.documents.filter((d) => d.id !== attachment.id) }
                                                : item
                                            )
                                          );
                                          setSelectedResearch((prev) =>
                                            prev ? { ...prev, documents: prev.documents.filter((d) => d.id !== attachment.id) } : null
                                          );
                                          toast.success("Attachment deleted");
                                        } catch (err: any) {
                                          toast.error(err.message || "Failed to delete attachment");
                                        }
                                      }}
                                      className="p-1.5 hover:bg-destructive/10 rounded-md transition-colors"
                                      title="Delete"
                                    >
                                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive transition-colors" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                         </div>
                       ) : (
                         <p className="text-xs text-muted-foreground italic px-2">No attachments found.</p>
                       )}

                       {canManageSelectedResearch && (
                           <div className="pt-3 border-t">
                           <FileUploadSection
                             id="research-selected-additional-upload"
                             helpText="Upload additional supporting documents. Files are uploaded immediately upon selection."
                             onFilesChange={(files) => uploadSelectedDocuments(files)}
                           />
                           {isUploadingSelectedDocuments && (
                             <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                               <Clock className="h-3 w-3 animate-spin" />
                               Uploading files...
                             </p>
                           )}
                         </div>
                       )}
                      </div>
                    </>
                  )}

                {/* Distribution Details (Analyst View) */}
                {userRole === "analyst" && selectedResearch.recipients && selectedResearch.recipients.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <Label>
                          Recipients ({selectedResearch.recipients.length})
                        </Label>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Eye className="h-4 w-4" />
                            {selectedResearch.recipients.filter(r => r.readAt).length} read
                          </div>
                          <div className="flex items-center gap-1">
                            <EyeOff className="h-4 w-4" />
                            {selectedResearch.recipients.filter(r => !r.readAt).length} unread
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {selectedResearch.recipients.map((recipient) => (
                          <div
                            key={recipient.id}
                            className="flex items-center justify-between p-3 border rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="text-xs">
                                  {recipient.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-sm">{recipient.name}</p>
                                <div className="flex items-center gap-2">
                                  <Badge
                                    variant="outline"
                                    className={
                                      recipient.role === "investor"
                                        ? "text-blue-700 border-blue-300 dark:text-blue-300 dark:border-blue-700"
                                        : "text-green-700 border-green-300 dark:text-green-300 dark:border-green-700"
                                    }
                                  >
                                    {recipient.role}
                                  </Badge>
                                  {recipient.firm && (
                                    <span className="text-xs text-muted-foreground">
                                      {recipient.firm}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {canManageSelectedResearch && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (!confirm(`Remove ${recipient.name} from recipients?`)) return;
                                    try {
                                      await researchService.deleteResearchRecipient(
                                        selectedResearch.id,
                                        currentUserId,
                                        recipient.id
                                      );
                                      setResearch((prev) =>
                                        prev.map((item) =>
                                          item.id === selectedResearch.id
                                            ? { ...item, recipients: item.recipients.filter((r) => r.id !== recipient.id) }
                                            : item
                                        )
                                      );
                                      setSelectedResearch((prev) =>
                                        prev ? { ...prev, recipients: prev.recipients.filter((r) => r.id !== recipient.id) } : null
                                      );
                                      toast.success("Recipient removed");
                                    } catch (err: any) {
                                      toast.error(err.message || "Failed to remove recipient");
                                    }
                                  }}
                                  className="p-1.5 hover:bg-destructive/10 rounded-md transition-colors"
                                  title="Remove recipient"
                                >
                                  <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive transition-colors" />
                                </button>
                              )}
                              <div className="text-right">
                                {recipient.readAt ? (
                                  <div className="flex items-center gap-2 text-sm text-green-600">
                                    <CheckCircle2 className="h-4 w-4" />
                                    <span>Read {recipient.readAt}</span>
                                  </div>
                                ) : (
                                  <Badge variant="outline">Unread</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-20 text-center text-muted-foreground">
                <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Select research to view details</p>
                <p className="text-sm">
                  Choose from the list to see full content and details
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Update Research</DialogTitle>
            <DialogDescription>
              Edit the selected research content and metadata.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Research Title *</Label>
              <Input
                placeholder="Research title"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Research Type *</Label>
              <Select value={editType} onValueChange={setEditType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="report">Research Report</SelectItem>
                  <SelectItem value="alert">Market Alert</SelectItem>
                  <SelectItem value="recommendation">Stock Recommendation</SelectItem>
                  <SelectItem value="commentary">Market Commentary</SelectItem>
                  <SelectItem value="analysis">Sector Analysis</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Summary *</Label>
              <Textarea
                placeholder="Brief summary..."
                rows={2}
                value={editSummary}
                onChange={(e) => setEditSummary(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Full Content *</Label>
              <Textarea
                placeholder="Research content"
                rows={8}
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={editPriority} onValueChange={setEditPriority}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Tags</Label>
                <Input
                  placeholder="Technology, AI, Growth"
                  value={editTags}
                  onChange={(e) => setEditTags(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Securities Covered</Label>
              <Input
                placeholder="NVDA, MSFT, AAPL"
                value={editSecurities}
                onChange={(e) => setEditSecurities(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Sectors Covered</Label>
              <MultiSelect
                options={SECTOR_FILTER_OPTIONS.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                selected={editSectors}
                onChange={setEditSectors}
                placeholder="Select sectors..."
              />
            </div>

            <Separator />

            <div className="space-y-3">
              <Label>Distribution Method</Label>
              <div className="grid grid-cols-3 gap-3">
                <Card
                  className={`cursor-pointer transition-all ${
                    editShareType === "individual"
                      ? "border-primary ring-2 ring-primary/20"
                      : "hover:border-primary/50"
                  }`}
                  onClick={() => setEditShareType("individual")}
                >
                  <CardContent className="p-3">
                    <div className="flex flex-col items-center gap-2 text-center">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                        <User className="h-4 w-4 text-blue-700 dark:text-blue-300" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Individual</p>
                        <p className="text-xs text-muted-foreground">Select clients</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className={`cursor-pointer transition-all ${
                    editShareType === "broadcast"
                      ? "border-primary ring-2 ring-primary/20"
                      : "hover:border-primary/50"
                  }`}
                  onClick={() => setEditShareType("broadcast")}
                >
                  <CardContent className="p-3">
                    <div className="flex flex-col items-center gap-2 text-center">
                      <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                        <Users className="h-4 w-4 text-purple-700 dark:text-purple-300" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Broadcast</p>
                        <p className="text-xs text-muted-foreground">All clients</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card
                  className={`cursor-pointer transition-all ${
                    editShareType === "public"
                      ? "border-primary ring-2 ring-primary/20"
                      : "hover:border-primary/50"
                  }`}
                  onClick={() => setEditShareType("public")}
                >
                  <CardContent className="p-3">
                    <div className="flex flex-col items-center gap-2 text-center">
                      <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                        <Globe className="h-4 w-4 text-green-700 dark:text-green-300" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Public</p>
                        <p className="text-xs text-muted-foreground">Anyone can view</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {editShareType === "individual" && (
              <div className="space-y-3">
                <Label>Select Recipients ({editSelectedRecipients.length} selected)</Label>
                <div className="border rounded-lg p-4 max-h-64 overflow-y-auto space-y-2">
                  {isLoadingClients ? (
                    <div className="py-6 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                      <Clock className="h-4 w-4 animate-spin text-primary" />
                      <span>Loading connected clients...</span>
                    </div>
                  ) : availableClients.length === 0 ? (
                    <div className="text-center py-4 text-sm text-muted-foreground">
                      No clients found
                    </div>
                  ) : (
                    <>
                      {/* Investors Section */}
                      {availableClients
                        .filter((c) => c.role === "investor")
                        .length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground mb-2">
                            INVESTORS
                          </p>
                          {availableClients
                            .filter((c) => c.role === "investor")
                            .map((client) => (
                              <div
                                key={client.id}
                                className="flex items-center space-x-3 p-2 hover:bg-muted rounded-lg cursor-pointer"
                                onClick={() => toggleEditRecipient(client.id)}
                              >
                                <Checkbox
                                  checked={editSelectedRecipients.includes(client.id)}
                                  onCheckedChange={() => toggleEditRecipient(client.id)}
                                />
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className="text-xs">
                                    {client.name
                                      .split(" ")
                                      .map((n) => n[0])
                                      .join("")}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                  <p className="text-sm font-medium">{client.name}</p>
                                  <Badge variant="outline" className="text-xs">
                                    Investor
                                  </Badge>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}

                      {availableClients.filter((c) => c.role === "investor").length > 0 &&
                        availableClients.filter((c) => c.role === "advisor").length > 0 && (
                          <Separator className="my-3" />
                        )}

                      {/* Advisors Section */}
                      {availableClients
                        .filter((c) => c.role === "advisor")
                        .length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground mb-2">
                            ADVISORS
                          </p>
                          {availableClients
                            .filter((c) => c.role === "advisor")
                            .map((client) => (
                              <div
                                key={client.id}
                                className="flex items-center space-x-3 p-2 hover:bg-muted rounded-lg cursor-pointer"
                                onClick={() => toggleEditRecipient(client.id)}
                              >
                                <Checkbox
                                  checked={editSelectedRecipients.includes(client.id)}
                                  onCheckedChange={() => toggleEditRecipient(client.id)}
                                />
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className="text-xs">
                                    {client.name
                                      .split(" ")
                                      .map((n) => n[0])
                                      .join("")}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1">
                                  <p className="text-sm font-medium">{client.name}</p>
                                  {client.firm && (
                                    <p className="text-xs text-muted-foreground">{client.firm}</p>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {editShareType === "broadcast" && (
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium mb-1">Broadcast Distribution</p>
                    <p className="text-sm text-muted-foreground">
                      This research will be shared with all {availableClients.length} clients
                      ({availableClients.filter((c) => c.role === "investor").length}{" "}
                      investors, {availableClients.filter((c) => c.role === "advisor").length}{" "}
                      advisors)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {editShareType === "public" && (
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium mb-1">Public Publication</p>
                    <p className="text-sm text-muted-foreground">
                      This research will be visible to all platform users including investors
                      and advisors you may not be directly connected with.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={updateSelectedResearch}>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Research</DialogTitle>
            <DialogDescription>
              This will permanently delete "{selectedResearch?.title}". This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={deleteSelectedResearch}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
